import { GREY_200, GREY_300, SPACING_3, SPACING_4, media } from '@mnd-frontend/ui';
import Debug from 'debug';
import React, { type ReactNode } from 'react';
import styled, { createGlobalStyle, css } from 'styled-components';
import UnscheduleRelatedMaterialModal from '../../../../components/UnscheduleRelatedMaterialModal';
import { useHiddenMenu } from '../../../../contexts/HiddenMenuContext';
import { SITE_CONTENT_RADIUS } from '../../../../lib/constants';
import { getCookieByName } from '../../../../lib/getCookieByName';
import Header from '../Header/index';
import Navigation2 from '../Navigation-2';
import NotLiveAlert from '../NotLiveAlert';
import SystemDialog from '../SystemDialog';
import Widgets from '../Widgets';

const GlobalStyles = createGlobalStyle<{ $newNavigationDisabled: boolean }>`
  ${({ $newNavigationDisabled }) =>
    !$newNavigationDisabled &&
    css`
      body {
        background-color: ${GREY_300};
      }
    `}
`;

const DebugInfo = React.lazy(() => import('../DebugInfo'));

if (typeof window !== 'undefined') {
  // eslint-disable-next-line no-underscore-dangle
  window.__debug = Debug;
}

const HeaderAndSiteContentWrapper = styled.div<{ $menuHidden: boolean }>`
  ${media.greaterThan('navigationBreakpoint')} {
    display: grid;
    grid-template-columns: ${({ $menuHidden }) =>
      $menuHidden ? '1fr' : 'var(--mnd-navigation-width) 1fr'};
    grid-template-rows: unset;
    min-height: 100%;
    margin: ${SPACING_3} ${SPACING_3} ${SPACING_3} 0;
    > * {
      min-width: 0;
    }
  }
`;

const SiteOverlays = styled.div`
  position: relative;
`;

const SiteContent = styled.div<{ $newNavigationDisabled: boolean; $menuHidden: boolean }>`
  ${props =>
    !props.$newNavigationDisabled && !props.$menuHidden
      ? css`
          background-color: ${GREY_200};
          min-height: 100vh;
          ${media.greaterThan('navigationBreakpoint')} {
            border-radius: ${SITE_CONTENT_RADIUS};
            padding-bottom: ${SPACING_4};
          }
          main {
            min-height: 100%;
            padding: 5rem 1rem;
            ${media.greaterThan('navigationBreakpoint')} {
              padding: 4rem 1rem 0;
            }
          }
        `
      : css`
          position: relative;
        `}
`;

const AppContainer = ({
  children,
  isMynewsdeskAdmin,
  newNavigationDisabled,
  pressroomId,
  sourceId,
  emailVerification,
}: {
  children: ReactNode;
  isMynewsdeskAdmin: boolean | undefined;
  newNavigationDisabled: boolean;
  pressroomId?: string;
  sourceId?: string;
  emailVerification?: boolean;
}) => {
  const { menuHidden } = useHiddenMenu();
  const newNavigationInformationBannerShown = getCookieByName(
    'new_navigation_information_banner_shown',
  );

  return (
    <>
      <GlobalStyles $newNavigationDisabled={newNavigationDisabled} />
      <SiteOverlays>
        <SystemDialog />
        <UnscheduleRelatedMaterialModal />
      </SiteOverlays>
      {!newNavigationDisabled ? (
        emailVerification ? (
          children
        ) : (
          <HeaderAndSiteContentWrapper $menuHidden={menuHidden}>
            {!menuHidden && (
              <Navigation2
                sourceId={sourceId}
                pressroomId={pressroomId}
                newNavigationInformationBannerShown={!!newNavigationInformationBannerShown}
                isMynewsdeskAdmin={isMynewsdeskAdmin}
              />
            )}
            <SiteContent $newNavigationDisabled={newNavigationDisabled} $menuHidden={menuHidden}>
              {pressroomId && <NotLiveAlert pressroomId={pressroomId} />}
              {children}
              {!emailVerification && <Widgets />}
            </SiteContent>
          </HeaderAndSiteContentWrapper>
        )
      ) : (
        <>
          {!emailVerification && !menuHidden && (
            <Header sourceId={sourceId} pressroomId={pressroomId} />
          )}
          <SiteContent $newNavigationDisabled={newNavigationDisabled} $menuHidden={menuHidden}>
            {pressroomId && <NotLiveAlert pressroomId={pressroomId} />}
            {children}
            {!emailVerification && <Widgets />}
          </SiteContent>
        </>
      )}

      {isMynewsdeskAdmin && newNavigationDisabled && <DebugInfo />}
    </>
  );
};

export default AppContainer;
